import produce from 'immer';

import {assert} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataType} from 'data-frames/DataFrameTypes';
import {AnalyticsVisualizationOutlierSetting as OutlierSetting} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {ScatterPlotProps} from 'ui-library/charting/ScatterPlot';
import {
  DETECTED_OUTLIER_LABEL,
  EXCLUDED_OUTLIER_LABEL,
} from 'ui-library/charting/statistics/outlier_detection/OutlierDetectionConstants';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import {CartesianPoint, Dataset, DatasetWithRegression, isLabeledDataset} from 'ui-library/charting/Types';

import {InsightsScatterPlotPoint} from './convertScatterPlotProps';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

function getRegressionAnalysisForDataset(
  dataset: Dataset<InsightsScatterPlotPoint>,
  regressionAnalyses: Array<SuccessfulAnalysis<RegressionAnalysis>>
): SuccessfulAnalysis<RegressionAnalysis> | null {
  if (
    !isLabeledDataset(dataset) ||
    (regressionAnalyses.length === 1 && regressionAnalyses[0].label == null)
  ) {
    // No partition
    return regressionAnalyses[0];
  } else {
    // Find regression analysis that matches the partition label
    const maybeAnalysis = regressionAnalyses.filter((analysis) => analysis.label === dataset.label);
    assert(maybeAnalysis.length <= 1, 'Expected no more than one regression analysis for the given dataset');
    return maybeAnalysis[0] ?? null;
  }
}

export default function addRegressionAnalysisToScatterPlotProps(
  props: ScatterPlotProps<InsightsScatterPlotPoint>,
  regressionAnalyses: Array<SuccessfulAnalysis<RegressionAnalysis>>,
  outlierConfig?: OutlierConfig | null
): ScatterPlotProps<InsightsScatterPlotPoint> {
  return produce(props, (draftProps) => {
    for (const dataset of draftProps.data.datasets as Array<
      DatasetWithRegression<CartesianPoint & {[SystemColumnName.ROW_ID]: JSONScalar}>
    >) {
      const regressionAnalysis = getRegressionAnalysisForDataset(dataset, regressionAnalyses);
      if (regressionAnalysis != null) {
        const detectedOutliers = regressionAnalysis.result.detectedOutliers;
        const isExcludeOutliers = outlierConfig?.outlierSetting === OutlierSetting.EXCLUDE;

        dataset.regressionAnalysis = regressionAnalysis;
        dataset.points = dataset.points.map((point) => ({
          ...point,
          ...(detectedOutliers != null && {
            isMasked: point.isMasked || (isExcludeOutliers && detectedOutliers.has(point.benchcol_row_id)),
            isOutlier: detectedOutliers.has(point.benchcol_row_id),
            pointWarning: detectedOutliers.has(point.benchcol_row_id)
              ? point.isMasked || (isExcludeOutliers && detectedOutliers.has(point.benchcol_row_id))
                ? EXCLUDED_OUTLIER_LABEL
                : DETECTED_OUTLIER_LABEL
              : null,
          }),
        }));

        draftProps.data.axisTypes = {
          ...draftProps.data.axisTypes,
          ...(detectedOutliers != null && {
            isOutlier: DataType.BOOLEAN,
            isMasked: DataType.BOOLEAN,
            pointWarning: DataType.STRING,
          }),
        };
      }
    }
  });
}
